import { useForm, SubmitHandler, FieldValues, Controller } from 'react-hook-form';
import Autocomplete from '@mui/material/Autocomplete';
import cn from 'classnames';

import BaseInput from 'components/common/BaseInput';
import HelperDesc from 'components/common/WarningInputMessage/WarningInputMessage';
import PhoneNumberField from 'components/common/PhoneNumberField/PhoneNumberField';
import { newEmployee } from 'views/ProviderProfile/types';
import styles from './PersonForm.module.scss';
import api from 'api';
import { PERMISSIONS_TO_ASSIGN_USER_ROLES, Role } from 'data/constants/roles';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

type Props = {
    initialFormValue: newEmployee;
    btnName: string;
    getFormData: (data: newEmployee) => void;
};

const PersonForm = ({ initialFormValue, btnName, getFormData }: Props) => {
    const {
        control,
        reset,
        formState: { errors, isDirty },
        handleSubmit,
    } = useForm<newEmployee>({
        mode: 'all',
        defaultValues: {
            firstname: initialFormValue.firstname,
            lastname: initialFormValue.lastname,
            middlename: initialFormValue.middlename,
            email: initialFormValue.email,
            phone_number: initialFormValue.phone_number,
            role: initialFormValue.role,
        },
    });

    const emailValidator: RegExp =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    const activeBtnCondition = isDirty && Object.keys(errors).length === 0;
    let msg = '';

    const user = useSelector((state: RootState) => state.user.entity);

    const roleOptions = PERMISSIONS_TO_ASSIGN_USER_ROLES[user.role as Role];

    const submitForm: SubmitHandler<FieldValues> = (data) => {
        const outputData: newEmployee = {
            firstname: data.firstname,
            lastname: data.lastname,
            middlename: data.middlename,
            email: data.email,
            phone_number: data.phone_number,
            role: data.role,
        };
        getFormData(outputData);
        reset(initialFormValue);
    };

    return (
        <form autoComplete="off" onSubmit={handleSubmit(submitForm)}>
            <h2 className={styles['form-title']}>Personal Info</h2>
            <div className={styles['form-block']}>
                <div className={styles['static-block']}>
                    <Controller
                        control={control}
                        name={'firstname'}
                        rules={{ required: 'Required field' }}
                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <BaseInput
                                label="*Collaborator Name"
                                fullWidth
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                error={error ? true : false}
                                helperText={
                                    error ? <HelperDesc message={error.message?.toString()} /> : ''
                                }
                                sx={{ mb: '28px' }}
                            />
                        )}
                    />

                    {/* <Controller
                        control={control}
                        name={'middlename'}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <BaseInput
                                label="Middle Name"
                                fullWidth
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                            />
                        )}
                    /> */}
                </div>
                <div className={styles['dynamic-block']}>
                    <Controller
                        control={control}
                        name={'lastname'}
                        rules={{
                            required: 'Required field',
                        }}
                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <BaseInput
                                label="*Last Name"
                                fullWidth
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                error={error ? true : false}
                                helperText={
                                    error ? <HelperDesc message={error.message?.toString()} /> : ''
                                }
                            />
                        )}
                    />
                </div>
            </div>
            <h2 className={styles['form-title']}>Contact details</h2>
            <div className={styles['form-block']}>
                <div className={styles['static-block']}>
                    <Controller
                        control={control}
                        name={'email'}
                        rules={{
                            required: 'Required field',
                            pattern: {
                                value: emailValidator,
                                message: 'Incorrect email',
                            },
                            validate: async (value) => {
                                try {
                                    await api.checkEmail.get(value);
                                } catch {
                                    msg = 'Email already in use';
                                }
                                return msg ? msg : undefined;
                            },
                        }}
                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <BaseInput
                                label="*Email"
                                fullWidth
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                error={error ? true : false}
                                helperText={
                                    error ? <HelperDesc message={error.message?.toString()} /> : ''
                                }
                            />
                        )}
                    />
                </div>

                <div className={styles['dynamic-block']}>
                    <Controller
                        control={control}
                        name={'phone_number'}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <PhoneNumberField
                                label="Phone number"
                                fullWidth
                                focusOnSelectCountry
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                            />
                        )}
                    />
                </div>
            </div>

            <h2 className={styles['form-title']}>User permission</h2>
            <div className={cn(styles['form-block'], styles['form-block_last'])}>
                <div className={styles['static-block']}>
                    <Controller
                        name={'role'}
                        control={control}
                        rules={{ required: 'Required field' }}
                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <Autocomplete
                                id={'role'}
                                options={roleOptions}
                                getOptionLabel={(option) => option}
                                renderInput={(params) => (
                                    <BaseInput
                                        {...params}
                                        label="*Role"
                                        onBlur={onBlur}
                                        error={error ? true : false}
                                        helperText={
                                            error ? (
                                                <HelperDesc message={error.message?.toString()} />
                                            ) : (
                                                ''
                                            )
                                        }
                                    />
                                )}
                                value={value}
                                onChange={(_, data) => {
                                    onChange(data as string);
                                    return data;
                                }}
                            />
                        )}
                    />
                </div>
                <div className={styles['dynamic-block']}></div>
            </div>

            <div className={styles['button-container']}>
                <button className={styles.btn} type="submit" disabled={!activeBtnCondition}>
                    {btnName}
                </button>
            </div>
        </form>
    );
};

export default PersonForm;
